import { compactDraft, createDefaultDraft, DRAFT_SCHEMA_VERSION, normalizeDraft } from '../domain/draft.js';
import { text } from '../domain/model-utils.js';

export class LocalDraftRepository {
  /** @param {{storage?: Storage, namespace?: string, clock?: () => string}} [options] */
  constructor(options = {}) {
    this.storage = options.storage || globalThis.localStorage;
    this.namespace = options.namespace || 'archai:deliverables:draft';
    this.clock = options.clock || (() => new Date().toISOString());
  }

  /** @param {string} projectId */
  keyFor(projectId) {
    return `${this.namespace}:v${DRAFT_SCHEMA_VERSION}:${encodeURIComponent(text(projectId))}`;
  }

  /** @param {string} projectId */
  async load(projectId) {
    const cleanProjectId = text(projectId);
    if (!cleanProjectId || !this.storage) return createDefaultDraft(cleanProjectId);
    const raw = this.storage.getItem(this.keyFor(cleanProjectId));
    if (!raw) return createDefaultDraft(cleanProjectId);
    try {
      const parsed = JSON.parse(raw);
      if (parsed.projectId !== cleanProjectId || parsed.schemaVersion !== DRAFT_SCHEMA_VERSION) {
        return createDefaultDraft(cleanProjectId);
      }
      return normalizeDraft(parsed, cleanProjectId);
    } catch {
      return createDefaultDraft(cleanProjectId);
    }
  }

  /** Supports save(draft) and save(projectId, draft). */
  async save(projectIdOrDraft, maybeDraft) {
    const draft = maybeDraft || projectIdOrDraft;
    const projectId = text(maybeDraft ? projectIdOrDraft : draft?.projectId);
    if (!projectId) throw new Error('Cannot save a deliverables draft without projectId.');
    const normalized = normalizeDraft(draft, projectId);
    const payload = { ...compactDraft(normalized), updatedAt: this.clock() };
    if (this.storage) this.storage.setItem(this.keyFor(projectId), JSON.stringify(payload));
    return normalized;
  }

  /** @param {string} projectId */
  async clear(projectId) {
    if (this.storage) this.storage.removeItem(this.keyFor(projectId));
  }

  /** @param {string} projectId */
  async remove(projectId) {
    return this.clear(projectId);
  }
}
